import { all, call, put, fork, select, take, takeEvery } from "typed-redux-saga";
import { isString } from "lodash-es";
import { daemonPrefix, HOME_PAGE_REDIRECT_URL } from "../../environments";
import { configurationTargetListFetched, configurationTargetListSet, configurationTargetSelectors, currentConfigurationTargetSet, fetchConfigurationTargetDataFinished, fetchConfigurationTargetDataStarted, supportedModelTypesSet } from "./configurationTargetSlice";
import { fetchContouringCustomizationsSaga } from "../contouring/contouringSagas";
import { fetchDaemonConfigsSaga } from "../daemon/daemonSagas";
import { fetchDoseCustomizationsSaga } from "../dose/doseSagas";
import { callApi } from "../sagas";
import { appConfigSelectors } from "../appConfig/appConfigSlice";
import { SupportedModelType, convertModelTypeToSupportedModelType } from "../appConfig/appDeploymentInfoTypes";
import { fetchAdaptCustomizationsSaga } from "../adapt/adaptSagas";
import { getApiErrorMessage } from "../../util/errors";
import { fetchImageCustomizationsSaga } from "../image/imageSagas";
import { activeTagFiltersReset as contourActiveTagFiltersReset, activeModalityFiltersReset as contourActiveModalityFiltersReset } from "../contouring/contouringSlice";
import { activeTagFiltersReset as doseActiveTagFiltersReset, activeModalityFiltersReset as doseActiveModalityFiltersReset } from "../dose/doseSlice";
import { activeTagFiltersReset as imageActiveTagFiltersReset, activeModalityFiltersReset as imageActiveModalityFiltersReset } from "../image/imageSlice";
import { activeTagFiltersReset as adaptActiveTagFiltersReset, activeModalityFiltersReset as adaptActiveModalityFiltersReset } from "../adapt/adaptSlice";
import { licenseStatusFetched, licenseStatusSet } from "../license/licenseSlice";
import { appStatusSelectors } from "../appStatus/appStatusSlice";
import routes, { doRedirect } from "../../routes";
import { LicenseStatus } from "../license/license-status";
import { selectIntersectionOfSupportedModelTypes } from "./configurationTargetSelectors";
import { ModalityDirection } from "../global-types/customization-types";
import { configurationTargetSettingsFetched } from "../configurationTargetSettings/configurationTargetSettingsSlice";


/** Initialization code that must be run whenever we change current configuration target */
export function* configurationTargetInitializationSaga() {

    // reset all current tag and modality filters -- "show deprecated" etc filters we're currently leaving intact
    yield* all([
        put(contourActiveTagFiltersReset()),
        put(doseActiveTagFiltersReset()),
        put(imageActiveTagFiltersReset()),
        put(adaptActiveTagFiltersReset()),

        put(contourActiveModalityFiltersReset(ModalityDirection.Input)),
        put(doseActiveModalityFiltersReset(ModalityDirection.Input)),
        put(imageActiveModalityFiltersReset(ModalityDirection.Input)),
        put(adaptActiveModalityFiltersReset(ModalityDirection.Input)),

        put(contourActiveModalityFiltersReset(ModalityDirection.Output)),
        put(doseActiveModalityFiltersReset(ModalityDirection.Output)),
        put(imageActiveModalityFiltersReset(ModalityDirection.Output)),
        put(adaptActiveModalityFiltersReset(ModalityDirection.Output)),
    ]);
}

/** Returns true when the app was loaded onto the /#/cloud bootstrap page. The hash is the
 *  source of truth for our HashRouter routes — we read it here (not via useLocation, which
 *  isn't available in sagas) so the API client list request can opt into the
 *  applicationsOnly filter only for this flow. */
const isCloudConfigBootstrapFlow = (): boolean => {
    const hash = typeof window !== 'undefined' ? window.location.hash : '';
    const path = hash.replace(/^#/, '').split('?')[0];
    return path === routes.cloudConfigPage.slug;
};

export function* fetchConfigurationTargetListSaga() {

    const applicationsOnly = isCloudConfigBootstrapFlow();

    yield* callApi({
        doApiCall: function* (client) {
            const configurationTargetList = yield* call(async () => client.fetchClientListAsync(applicationsOnly));
            return configurationTargetList;
        },
        onSuccess: function* (result) {
            yield* put(configurationTargetListSet({ configurationTargetList: result }));

            // immediately set current app client in certain conditions
            // (re-select client list here in case we do any post-processing
            // for the list in the reducer in future)
            const configurationTargetList = yield* select(configurationTargetSelectors.selectConfigurationTargetList);
            if (configurationTargetList !== undefined) {
                if (configurationTargetList.length === 1) {
                    // if we only have one item available, auto-select it
                    yield* put(currentConfigurationTargetSet(configurationTargetList[0].userId));
                } else {
                    // if there's exactly one daemon client available, auto-select it
                    const daemons = configurationTargetList.filter(c => c.userName.startsWith(daemonPrefix));
                    if (daemons.length === 1) {
                        yield* put(currentConfigurationTargetSet(daemons[0].userId));
                    }
                }
            }
        },
        onFailure: function* (error) {
            console.error('Was not able to fetch client list');
            // TODO: this format might be cockpit-specific? In that case it must be fixed everywhere in this file.
            const errorMessage = isString(error) ? error : getApiErrorMessage(error);
            yield* put(configurationTargetListSet({ configurationTargetList: null, errorMessage: `An error occurred when trying to retrieve client list: ${errorMessage}` }));
        }
    });
}

function* watchFetchConfigurationTargetListSaga() {
    yield* takeEvery(configurationTargetListFetched, fetchConfigurationTargetListSaga);
}

/** Watch when current configuration target is changed and re-fetch any data related to previous target as well. */
function* watchCurrentConfigurationTargetListSet() {
    while (true) {
        yield* take(currentConfigurationTargetSet);
        const currentTarget = yield* select(configurationTargetSelectors.selectCurrent);
        const currentModelType = yield* select(appStatusSelectors.selectCurrentAppModelType);

        // block UI while fetching new values
        yield* put(fetchConfigurationTargetDataStarted());

        // queue re-fetches (currently just model and daemon configs, licenses are gotten through the page component instead of here)
        // (only fetch model types that are supported in current deployment)
        const appDeploymentInfo = yield* select(appConfigSelectors.selectAppDeploymentInfo);
        if (!appDeploymentInfo) { throw new Error('Application is not initialized properly -- cannot fetch data from backend'); }

        const modelTypeFetchCalls = [];
        if (appDeploymentInfo.supportedModelTypes.includes(SupportedModelType.Contour)) {
            modelTypeFetchCalls.push(call(fetchContouringCustomizationsSaga, currentTarget));
        }
        if (appDeploymentInfo.supportedModelTypes.includes(SupportedModelType.Dose)) {
            modelTypeFetchCalls.push(call(fetchDoseCustomizationsSaga, currentTarget));
        }
        if (appDeploymentInfo.supportedModelTypes.includes(SupportedModelType.Image)) {
            modelTypeFetchCalls.push(call(fetchImageCustomizationsSaga, currentTarget));
        }
        if (appDeploymentInfo.supportedModelTypes.includes(SupportedModelType.Adapt)) {
            modelTypeFetchCalls.push(call(fetchAdaptCustomizationsSaga, currentTarget));
        }

        yield* all([
            ...modelTypeFetchCalls,
            call(fetchDaemonConfigsSaga, currentTarget),
            put(configurationTargetSettingsFetched()),
            put(licenseStatusFetched(currentTarget))
        ]);

        // re-fetching is done
        yield* put(fetchConfigurationTargetDataFinished());

        // Check if current model type is supported by both deployment info and license
        if (currentModelType) {
            const supportedModelType = convertModelTypeToSupportedModelType(currentModelType);
            if (supportedModelType) {
                const intersectionOfSupportedModelTypes = yield* select(selectIntersectionOfSupportedModelTypes);
                if (!intersectionOfSupportedModelTypes.includes(supportedModelType)) {
                    // Current model type is not supported by both deployment info and license, redirect to home page
                    yield* call(doRedirect, HOME_PAGE_REDIRECT_URL);
                }
            }
        }
    }
}

function* watchFetchConfigurationTargetDataFinished() {
    // run initialization whenever configuration target is changed
    yield* takeEvery(fetchConfigurationTargetDataFinished, configurationTargetInitializationSaga);
}

/** Compute supported model types based on license status and update the store */
function* computeSupportedModelTypesSaga(licenseStatus: LicenseStatus[] | null) {
    if (!licenseStatus) {
        yield* put(supportedModelTypesSet(undefined));
        return;
    }

    const supportedModelTypes: SupportedModelType[] = [];
    const licensedModelTypes = licenseStatus.map(license => license.modelTypes).flat() || [];
    // Remove duplicates from licensedModelTypes
    const uniqueModelTypes = [...new Set(licensedModelTypes)];
    uniqueModelTypes.forEach(modelType => {
        const actualModelType = convertModelTypeToSupportedModelType(modelType);
        if (actualModelType) {
            supportedModelTypes.push(actualModelType);
        }
    });
    yield* put(supportedModelTypesSet(supportedModelTypes));
}

/** Watch for license status changes and update supported model types accordingly */
function* watchLicenseStatusSetSaga() {
    while (true) {
        const action = yield* take(licenseStatusSet);
        if (licenseStatusSet.match(action)) {
            yield* fork(computeSupportedModelTypesSaga, action.payload.licenseStatus);
        }
    }
}

/** Returns all relevant watches to be added to a main root watch saga */
export function getWatchesForConfigurationTargetSagas() {
    return [
        watchFetchConfigurationTargetListSaga(),
        watchCurrentConfigurationTargetListSet(),
        watchFetchConfigurationTargetDataFinished(),
        watchLicenseStatusSetSaga(),
    ];
}
